/**
 * Sdílená logika doplnění pozvánky z dat, která už v aplikaci jsou:
 * - weddings (název, datum, místo, uvítání)
 * - guests s role bride / groom (Svatební tým)
 * - ceremony_meta (čas obřadu, název místa, adresa)
 */

export interface WeddingRow {
  title: string;
  date: string | null;
  location: string;
  welcome_message?: string | null;
}

export interface GuestNameRow {
  full_name: string;
  role: string;
}

export interface CeremonyMetaRow {
  start_time?: string | null;
  location_name?: string | null;
  location_address?: string | null;
}

function publicConfigObject(v: unknown): Record<string, unknown> {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return {};
}

/**
 * Supabase vrací `public_config`; v paměti často `publicConfig`.
 * Sloučí obě: `public_config` má přednost u stejných klíčů (zdroj v DB).
 * Čisté `??` nestačí — prázdný `{}` v jednom poli by jinak přebil druhé.
 */
export function getStoredPublicConfigFromWedding(wedding: {
  public_config?: unknown;
  publicConfig?: unknown;
}): Record<string, unknown> {
  const camel = publicConfigObject(wedding.publicConfig);
  const snake = publicConfigObject(wedding.public_config);
  return { ...camel, ...snake };
}

export interface InvitationFormState {
  public_enabled: boolean;
  public_slug: string;
  brideName: string;
  groomName: string;
  bridePhotoUrl: string;
  groomPhotoUrl: string;
  ceremonyTime: string;
  ceremonyVenue: string;
  fullAddress: string;
  mapsUrl: string;
  dressCode: string;
  heroImageUrl: string;
  welcomeMessage: string;
  storyText: string;
  showSchedule: boolean;
  showFaq: boolean;
  showCatering: boolean;
  cateringIntro: string;
  cateringShowMain: boolean;
  cateringMainText: string;
  cateringShowWarmBuffet: boolean;
  cateringWarmBuffetText: string;
  cateringShowColdBuffet: boolean;
  cateringColdBuffetText: string;
  cateringShowSweetBar: boolean;
  cateringSweetBarText: string;
  cateringShowDrinks: boolean;
  cateringDrinksText: string;
  cateringMainPhotoUrl: string;
  cateringWarmPhotoUrl: string;
  cateringColdPhotoUrl: string;
  cateringSweetPhotoUrl: string;
  cateringDrinksPhotoUrl: string;
  showMusic: boolean;
  musicIntro: string;
  musicLiveDj: boolean;
  musicFromSpeakers: boolean;
  musicSpotifyUrl: string;
  musicPlaylistCta: string;
  showEntertainment: boolean;
  showAccommodationInfo: boolean;
  accommodationInfo: string;
  accommodationMapsUrl: string;
  showGuestDetailsForm: boolean;
  guestFormIntro: string;
  guestFormShowAccommodation: boolean;
  guestFormShowAllergies: boolean;
  guestFormShowChildren: boolean;
  guestFormShowGuestNote: boolean;
}

/** Výsledek merge pro API a PublicPage. */
export interface MergedPublicInvitationConfig {
  showSchedule: boolean;
  showFaq: boolean;
  welcomeMessage: string;
  brideName: string | null;
  groomName: string | null;
  bridePhotoUrl: string | null;
  groomPhotoUrl: string | null;
  ceremonyTime: string | null;
  ceremonyVenue: string | null;
  fullAddress: string | null;
  mapsUrl: string | null;
  dressCode: string | null;
  heroImageUrl: string | null;
  storyText: string | null;
  showCatering: boolean;
  cateringIntro: string | null;
  cateringShowMain: boolean;
  cateringMainText: string | null;
  cateringShowWarmBuffet: boolean;
  cateringWarmBuffetText: string | null;
  cateringShowColdBuffet: boolean;
  cateringColdBuffetText: string | null;
  cateringShowSweetBar: boolean;
  cateringSweetBarText: string | null;
  cateringShowDrinks: boolean;
  cateringDrinksText: string | null;
  cateringMainPhotoUrl: string | null;
  cateringWarmPhotoUrl: string | null;
  cateringColdPhotoUrl: string | null;
  cateringSweetPhotoUrl: string | null;
  cateringDrinksPhotoUrl: string | null;
  showMusic: boolean;
  musicIntro: string | null;
  musicLiveDj: boolean;
  musicFromSpeakers: boolean;
  musicSpotifyUrl: string | null;
  musicPlaylistCta: string | null;
  showEntertainment: boolean;
  entertainmentIntro: string | null;
  showAccommodationInfo: boolean;
  accommodationInfo: string | null;
  accommodationMapsUrl: string | null;
  showGuestDetailsForm: boolean;
  guestFormIntro: string | null;
  guestFormShowAccommodation: boolean;
  guestFormShowAllergies: boolean;
  guestFormShowChildren: boolean;
  guestFormShowGuestNote: boolean;
}

/** Normalizace slugu — stejná logika jako v Nastavení → Pozvánka. */
export function normalizePublicSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Návrh slugu z názvu svatby a roku (když uživatel ještě slug nevyplnil). */
export function suggestSlugFromWedding(wedding: WeddingRow): string {
  const year = wedding.date ? wedding.date.slice(0, 4) : '';
  const base = year ? `${wedding.title} ${year}` : wedding.title;
  return normalizePublicSlug(base);
}

function timeHHmm(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s || null;
}

function pickBrideGuest(guests: GuestNameRow[]): GuestNameRow | null {
  return guests.find((g) => g.role === 'bride') ?? null;
}

function pickGroomGuest(guests: GuestNameRow[]): GuestNameRow | null {
  return guests.find((g) => g.role === 'groom') ?? null;
}

function cfgBool(cfg: Record<string, unknown>, key: string, defaultVal: boolean): boolean {
  return typeof cfg[key] === 'boolean' ? (cfg[key] as boolean) : defaultVal;
}

/**
 * Sloučí uložené public_config s fallbacky z DB (bez přepsání explicitně uložených hodnot).
 */
export function mergePublicInvitationConfig(
  stored: Record<string, unknown> | null | undefined,
  wedding: WeddingRow,
  teamGuests: GuestNameRow[],
  ceremonyMeta: CeremonyMetaRow | null,
): MergedPublicInvitationConfig {
  const cfg = stored ?? {};
  const bride = pickBrideGuest(teamGuests);
  const groom = pickGroomGuest(teamGuests);
  const loc = (wedding.location || '').trim();
  const metaVenue = (ceremonyMeta?.location_name || '').trim();
  const metaAddr = (ceremonyMeta?.location_address || '').trim();

  const ceremonyTimeFallback =
    timeHHmm(ceremonyMeta?.start_time as string | undefined) ?? null;
  const ceremonyVenueFallback = metaVenue || loc || null;
  const fullAddressFallback = metaAddr || loc || null;

  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

  return {
    showSchedule: typeof cfg.showSchedule === 'boolean' ? cfg.showSchedule : true,
    showFaq: typeof cfg.showFaq === 'boolean' ? cfg.showFaq : true,
    welcomeMessage:
      str(cfg.welcomeMessage) ??
      (wedding.welcome_message && String(wedding.welcome_message).trim()) ??
      '',
    brideName: str(cfg.brideName) ?? (bride?.full_name ? bride.full_name.trim() : null),
    groomName: str(cfg.groomName) ?? (groom?.full_name ? groom.full_name.trim() : null),
    bridePhotoUrl: str(cfg.bridePhotoUrl),
    groomPhotoUrl: str(cfg.groomPhotoUrl),
    ceremonyTime: str(cfg.ceremonyTime) ?? ceremonyTimeFallback,
    ceremonyVenue: str(cfg.ceremonyVenue) ?? ceremonyVenueFallback,
    fullAddress: str(cfg.fullAddress) ?? fullAddressFallback,
    mapsUrl: str(cfg.mapsUrl),
    dressCode: str(cfg.dressCode),
    heroImageUrl: str(cfg.heroImageUrl),
    storyText: str(cfg.storyText),
    showCatering: cfgBool(cfg, 'showCatering', false),
    cateringIntro: str(cfg.cateringIntro),
    cateringShowMain: cfgBool(cfg, 'cateringShowMain', false),
    cateringMainText: str(cfg.cateringMainText),
    cateringShowWarmBuffet: cfgBool(cfg, 'cateringShowWarmBuffet', false),
    cateringWarmBuffetText: str(cfg.cateringWarmBuffetText),
    cateringShowColdBuffet: cfgBool(cfg, 'cateringShowColdBuffet', false),
    cateringColdBuffetText: str(cfg.cateringColdBuffetText),
    cateringShowSweetBar: cfgBool(cfg, 'cateringShowSweetBar', false),
    cateringSweetBarText: str(cfg.cateringSweetBarText),
    cateringShowDrinks: cfgBool(cfg, 'cateringShowDrinks', false),
    cateringDrinksText: str(cfg.cateringDrinksText),
    cateringMainPhotoUrl: str(cfg.cateringMainPhotoUrl),
    cateringWarmPhotoUrl: str(cfg.cateringWarmPhotoUrl),
    cateringColdPhotoUrl: str(cfg.cateringColdPhotoUrl),
    cateringSweetPhotoUrl: str(cfg.cateringSweetPhotoUrl),
    cateringDrinksPhotoUrl: str(cfg.cateringDrinksPhotoUrl),
    showMusic: cfgBool(cfg, 'showMusic', false),
    musicIntro: str(cfg.musicIntro),
    musicLiveDj: cfgBool(cfg, 'musicLiveDj', false),
    musicFromSpeakers: cfgBool(cfg, 'musicFromSpeakers', false),
    musicSpotifyUrl: str(cfg.musicSpotifyUrl),
    musicPlaylistCta: str(cfg.musicPlaylistCta),
    showEntertainment: cfgBool(cfg, 'showEntertainment', false),
    entertainmentIntro: str(cfg.entertainmentIntro),
    showAccommodationInfo: cfgBool(cfg, 'showAccommodationInfo', false),
    accommodationInfo: str(cfg.accommodationInfo),
    accommodationMapsUrl: str(cfg.accommodationMapsUrl),
    showGuestDetailsForm: cfgBool(cfg, 'showGuestDetailsForm', false),
    guestFormIntro: str(cfg.guestFormIntro),
    guestFormShowAccommodation: cfgBool(cfg, 'guestFormShowAccommodation', false),
    guestFormShowAllergies: cfgBool(cfg, 'guestFormShowAllergies', false),
    guestFormShowChildren: cfgBool(cfg, 'guestFormShowChildren', false),
    guestFormShowGuestNote: cfgBool(cfg, 'guestFormShowGuestNote', false),
  };
}

/**
 * Po načtení svatby + hostů + ceremony_meta doplní do formuláře jen prázdná pole
 * (už vyplněná nebo uložená v public_config se nepřepisují).
 */
export function applyInvitationDefaultsToForm(
  prev: InvitationFormState,
  wedding: WeddingRow,
  storedConfig: Record<string, unknown> | null | undefined,
  teamGuests: GuestNameRow[],
  ceremonyMeta: CeremonyMetaRow | null,
): InvitationFormState {
  const merged = mergePublicInvitationConfig(storedConfig, wedding, teamGuests, ceremonyMeta);
  return {
    ...prev,
    brideName: prev.brideName.trim() || merged.brideName || '',
    groomName: prev.groomName.trim() || merged.groomName || '',
    ceremonyTime: prev.ceremonyTime.trim() || merged.ceremonyTime || '',
    ceremonyVenue: prev.ceremonyVenue.trim() || merged.ceremonyVenue || '',
    fullAddress: prev.fullAddress.trim() || merged.fullAddress || '',
    welcomeMessage: prev.welcomeMessage.trim() || merged.welcomeMessage || '',
    storyText: prev.storyText.trim() || merged.storyText || '',
    dressCode: prev.dressCode.trim() || merged.dressCode || '',
    mapsUrl: prev.mapsUrl.trim() || merged.mapsUrl || '',
    heroImageUrl: prev.heroImageUrl.trim() || merged.heroImageUrl || '',
    bridePhotoUrl: prev.bridePhotoUrl.trim() || merged.bridePhotoUrl || '',
    groomPhotoUrl: prev.groomPhotoUrl.trim() || merged.groomPhotoUrl || '',
    public_slug: prev.public_slug.trim() || suggestSlugFromWedding(wedding) || '',
  };
}
